import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import PageHeader from "@/components/base/PageHeader";
import SectionHeading from "@/components/base/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { testimonials } from "@/mocks/testimonials";
import { Link } from "react-router-dom";

const principles = [
  {
    icon: "ri-compass-3-line",
    title: "只做整理，不搬运",
    text: "本站不提供漫画内容搬运，只做追更进度、话数整理与剧情速览，阅读请前往正规平台。",
  },
  {
    icon: "ri-focus-3-line",
    title: "进度清晰优先",
    text: "以追更前先看进度为核心，任何信息都围绕更新到哪、第几话、完结没有来组织。",
  },
  {
    icon: "ri-verified-badge-line",
    title: "内容严谨可查",
    text: "剧情与设定整理以原著与主线为准，条理清晰、便于核对，不编造不误导。",
  },
];

const contacts = [
  {
    icon: "ri-mail-line",
    label: "联系邮箱",
    value: "contact@example.com",
    href: "mailto:contact@example.com",
  },
  {
    icon: "ri-message-3-line",
    label: "反馈建议",
    value: "欢迎就进度整理提出意见",
    href: "/#subscribe",
  },
  {
    icon: "ri-book-2-line",
    label: "版权说明",
    value: "内容整理自公开信息",
    href: "/about",
  },
];

export default function About() {
  useSeo({
    title: "关于斗罗漫报 · 斗罗大陆漫画追更信息站",
    description:
      "了解斗罗漫报：专注于斗罗大陆漫画追更进度与最新剧情整理的信息聚合站。品牌介绍、网站定位、发展理念、内容原则与联系方式一页看全。",
    keywords: "斗罗漫报,斗罗大陆漫画追更,斗罗大陆漫画信息站,关于我们,剧情整理",
    canonicalPath: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://dcomx.github.io/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://dcomx.github.io/about" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于斗罗漫报",
        url: "https://dcomx.github.io/about",
        inLanguage: "zh-CN",
        about: {
          "@type": "Organization",
          name: "斗罗漫报",
          description: "专注斗罗大陆漫画追更进度与最新剧情整理的信息聚合站。",
          email: "contact@example.com",
        },
      },
    ],
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <PageHeader
          breadcrumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
          title="关于斗罗漫报"
          subtitle="一个专注斗罗大陆漫画追更进度与最新剧情整理的信息聚合站，为追更读者提供清晰、省心的进度速览。"
          image="https://readdy.ai/api/search-image?query=An%20ancient%20ink%20brush%20and%20scroll%20desk%20in%20a%20moonlit%20study%20overlooking%20a%20misty%20fantasy%20world%2C%20chinese%20literati%20aesthetic%2C%20warm%20amber%20candlelight%2C%20ink%20wash%20mixed%20with%20vibrant%20color%2C%20contemplative%20mood%2C%20high%20detail&width=1600&height=640&seq=about-header&orientation=landscape"
          alt="斗罗漫报关于我们背景"
        />

        <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
            <div className="relative min-h-[300px] overflow-hidden rounded-lg">
              <img
                src="https://readdy.ai/api/search-image?query=A%20young%20reader%20with%20a%20scroll%20sitting%20under%20a%20glowing%20fantasy%20tree%20with%20floating%20story%20pages%20around%2C%20chinese%20fantasy%20reader%20mood%2C%20warm%20amber%20and%20cream%20tones%2C%20ink%20wash%20mixed%20with%20vibrant%20color%2C%20high%20detail&width=1000&height=700&seq=about-intro&orientation=portrait"
                alt="斗罗漫报的品牌故事插画"
                className="h-full w-full object-cover"
              />
            </div>
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-accent-600">
                <span className="h-px w-6 bg-accent-500" />
                品牌介绍
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                我们想做的，是让追更更省心
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                <p>
                  追更一部作品，最怕的就是忘了更新到哪、不知道讲到哪里、不确定完结没有。斗罗漫报正是围绕这个痛点而生：把《斗罗大陆》漫画的追更进度、话数整理与最新剧情，清晰、有条理地呈现给你。
                </p>
                <p>
                  我们不做内容搬运，只做信息整理与速览，帮助你快速掌握进度后再前往正规平台安心阅读，追更不迷路。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-14 md:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <SectionHeading
              kicker="定位与理念"
              title="把复杂进度，整理成看得懂的脉络"
              desc="以清晰、省心、严谨为原则，服务每一位追更读者。"
            />
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {principles.map((p) => (
                <div key={p.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-500/10 text-primary-600">
                    <i className={`${p.icon} text-xl`} />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <SectionHeading
            kicker="用户口碑"
            title="追更读者对我们的评价"
            desc="来自真实追更场景的使用感受，也是我们持续改进的动力。"
          />
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-6">
                <div className="flex items-center gap-1 text-accent-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm" />
                  ))}
                </div>
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">
                  “{t.text}”
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50">
                    {t.name.charAt(0)}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-400">{t.tag}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section id="contact" className="bg-background-100 py-14 md:py-20">
          <div className="mx-auto w-full max-w-5xl px-4 md:px-6">
            <SectionHeading
              kicker="联系方式"
              title="与我们联系"
              desc="有任何进度整理上的建议或疑问，欢迎通过以下方式联系我们。"
            />
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-3">
              {contacts.map((c) => (
                <a
                  key={c.label}
                  href={c.href}
                  className="flex flex-col items-center rounded-lg border border-background-200 bg-background-50 p-6 text-center transition-colors hover:border-primary-300"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-500/15 text-accent-600">
                    <i className={`${c.icon} text-xl`} />
                  </span>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-950">{c.label}</h3>
                  <p className="mt-1 text-xs text-foreground-500">{c.value}</p>
                </a>
              ))}
            </div>
            <div className="mt-10 rounded-lg bg-primary-500 p-6 text-center md:p-8">
              <h3 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
                想第一时间掌握剧情更新？
              </h3>
              <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-background-100/85">
                订阅追更提醒，进度更新时第一时间通知你，追更不迷路。
              </p>
              <Link
                to="/#subscribe"
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
              >
                去订阅追更提醒
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}